import { FranchiseStatus } from "../shared/enum/franchise-status.enum";
import { LocationAvailabilityEnum } from "../shared/enum/location-availability";
import { UserStatus } from "../shared/enum/user-status";
import { PartnerStatus } from "../shared/enum/partner-status";
import { RemunerationEnum } from "../shared/enum/renumeration.enum";
import { LocationWithAvailabilities } from "../shared/models/advertising-campaigns/advertising-campaign.interface";
import { DateFilter } from "../shared/models/date-filte.interface";
import { LocationPartnerInterface } from "../shared/models/location-partners/locationPartner.interface";
import { RetentionRate } from "../shared/models/statistics/retentionRate";
import {
  ExportData,
  exportDataType,
  RetentionRateData,
} from "../shared/data/exportData";
import { TranslateService } from "@ngx-translate/core";
import { JwtService } from "../shared/services/jwt.service";
import { SubRoleEnum } from "../shared/models/user/sub_role.interface";
import { Constants } from "../shared/data/constants";
import { CustomFields } from "../shared/models/ClickUp/createTaskRequestDto";
import { FormControl, FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { AdvertisingCampaignRemunerationDetails } from "../shared/models/advertising-campaigns/advertisingCampaignRemunerationDetails.interface";
import { DatePipe } from "@angular/common";

export class UtilFunctions {
  /*
    Convert an ArrayBuffer into a string
    from https://developer.chrome.com/blog/how-to-convert-arraybuffer-to-and-from-string/
    */
  public static ab2str(buf: ArrayBuffer) {
    return String.fromCharCode(...new Uint8Array(buf));
  }

  /*
    Export the given key and write it into the "exported-key" space.
    */

  constructor(
    private translate: TranslateService,
    private datePipe: DatePipe
  ) {}

  public static async exportCryptoKey(key: CryptoKey, isPrivate: boolean) {
    const exported = await window.crypto.subtle.exportKey(
      isPrivate ? "pkcs8" : "spki",
      key
    );
    const exportedAsString = this.ab2str(exported);
    const exportedAsBase64 = window.btoa(exportedAsString);
    const keyType = isPrivate ? "PRIVATE" : "PUBLIC";
    const pemExported = `-----BEGIN ${keyType} KEY-----\n${exportedAsBase64}\n-----END ${keyType} KEY-----`;

    return pemExported;
  }

  public static sortCSVDataString(csvData: string): string {
    const csvRows = csvData.split("\n");
    const dataHeader = csvRows[0];
    const sortedData = csvRows.slice(1).sort();
    const sortedString = dataHeader + "\n" + sortedData.join("\n");
    return sortedString;
  }

  public static convertToCSV(data: any[]): string {
    const csvRows: string[] = [];
    let headers = Object.keys(data[0]);
    csvRows.push(headers.join(","));

    for (const row of data) {
      const values = headers.map((header) => {
        const val = row[header];
        return `${val}`;
      });
      csvRows.push(values.join(","));
    }

    return csvRows.join("\n");
  }

  public static toPascalCaseWithSpaces(str: string): string {
    return str
      .match(
        /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g
      )!
      .map((x) => x.slice(0, 1).toUpperCase() + x.slice(1).toLowerCase())
      .join(" ");
  }

  public static getAvailabilityStatus(
    companyName: string,
    locationsWithAvailabilities: LocationWithAvailabilities[],
    startDate: Date,
    endDate: Date
  ): LocationAvailabilityEnum {
    return this.isAvailable(
      locationsWithAvailabilities.find((loc) => loc.companyName == companyName)
        ?.listOfAvailableDates,
      startDate,
      endDate
    );
  }

  public static isAvailable(
    dates: DateFilter[] | undefined,
    startDate: Date,
    endDate: Date
  ): LocationAvailabilityEnum {
    let fullyCovered = false;
    let partiallyCovered = false;

    if (!dates || dates.length === 0) {
      return LocationAvailabilityEnum.NOT_AVAILABLE;
    }

    const adStart = new Date(startDate);
    const adEnd = new Date(endDate);

    for (const filter of dates) {
      if (filter.startDate && filter.endDate) {
        const filterStart = new Date(filter.startDate);
        const filterEnd = new Date(filter.endDate);

        if (filterStart <= adStart && filterEnd >= adEnd) {
          fullyCovered = true;
        }

        const overlaps =
          (filterStart <= adStart && filterEnd >= adStart) ||
          (filterStart <= adEnd && filterEnd >= adEnd) ||
          (filterStart >= adStart && filterEnd <= adEnd);

        if (overlaps) {
          partiallyCovered = true;
        }
      }
    }

    if (fullyCovered) return LocationAvailabilityEnum.AVAILABLE;
    if (partiallyCovered) return LocationAvailabilityEnum.PARTIALLY;

    return LocationAvailabilityEnum.NOT_AVAILABLE;
  }

  public static isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  public static isCollaboratorArchived(archivedDate: Date | null): UserStatus {
    if (archivedDate) {
      return UserStatus.ARCHIVED;
    } else {
      return UserStatus.ACTIVE;
    }
  }

  public static replaceSpacesWithHyphen(input: string): string {
    return input.replace(/\s+/g, "-");
  }

  public static getFranchisedActiveStatus(
    franchised: LocationPartnerInterface
  ): FranchiseStatus {
    const unfinalizedFranchised =
      franchised.partnerStatus === null ||
      (franchised.partnerStatus === PartnerStatus.REVENUE_SHARING &&
        franchised.pilotPhaseEndDate === null) ||
      (franchised.partnerStatus === PartnerStatus.INTERNET_PROVIDER_PAYMENT &&
        franchised.paymentFrequency === null) ||
      franchised.sessionTimeoutInMinutes === null ||
      franchised.routerBrand === null;

    return unfinalizedFranchised
      ? FranchiseStatus.AWAITING_CONFIRMATION
      : FranchiseStatus.ACTIVE;
  }

  public static exportRetentionRate(
    retentionRate: RetentionRate,
    translate: TranslateService
  ) {
    let csvRows: string[] = [];
    let headers = [];
    let retentionChart: ExportData[] = RetentionRateData;
    headers = [
      translate.instant("Charts.retentionRate.week"),
      translate.instant("Charts.retentionRate.week"),
      translate.instant("Charts.retentionRate.yaxisTitle"),
    ];
    for (let i = 0; i < retentionRate.weeks.length; i++) {
      const cols = [retentionRate.weeks[i], retentionRate.retentionRate[i]];
      csvRows.push(cols.join(","));
    }
    for (let i = 0; i < retentionChart.length; i++) {
      if (retentionChart[i].type == exportDataType.RETENTION_RATE)
        retentionChart[i].data = [headers, ...csvRows].join("\n");
    }
    return retentionChart;
  }

  public static getSessionDurationParts(
    sessionDurationMs: number
  ): { value: number; key: string }[] {
    const totalMinutes = Math.floor(sessionDurationMs / 60000);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const parts: { value: number; key: string }[] = [];

    if (hours > 0) {
      parts.push({
        value: hours,
        key: "admin.locations.repository.sessionTime.HOURS",
      });
    }

    if (minutes > 0) {
      parts.push({
        value: minutes,
        key: "admin.locations.repository.sessionTime.MINUTES",
      });
    }

    return parts;
  }

  public static isFranchised(jwtService: JwtService) {
    const userSubRole = jwtService.getSubRoleName(jwtService.getToken());
    return userSubRole === SubRoleEnum.FRANCHISED;
  }

  public static calculateAverage(value: number, percentage: number) {
    return value * percentage;
  }

  public static companyActivityName(translate: TranslateService) {
    if (translate.currentLang == "fr") return Constants.IBERIS_ACTIVITIES_FR;
    else return Constants.IBERIS_ACTIVITIES_EN;
  }

  public static customField(
    id: string,
    value: string[] | string | number | null
  ) {
    const customFields: CustomFields = {
      id: id,
      value: value,
    };
    return customFields;
  }

  public static totalImpressionsMin(totalImpressions: number) {
    return Math.ceil(totalImpressions * 0.95);
  }

  public static totalImpressionsMax(totalImpressions: number) {
    return Math.ceil(totalImpressions * 1.05);
  }

  public static formatToKValue(num: number): string {
    if (num >= 1000) {
      const divided = num / 1000;
      const formatted =
        divided % 1 === 0 ? divided.toFixed(0) : divided.toFixed(1);
      return formatted + "K";
    } else {
      if (Number(num.toFixed(0)) - num !== 0) {
        return num.toFixed(2);
      } else {
        return num.toString();
      }
    }
  }

  public static formValidator(form: FormGroup, field: string) {
    return form.get(field)?.valid ?? false;
  }

  public static currentRoute(router: Router): string[] {
    return router.url.split("/").filter((p) => p !== "");
  }

  public static adCompletionRate(completion: number, views: number): number {
    if (!views || views === 0) {
      return 0;
    }
    return Math.round((completion / views) * 100);
  }

  public static showTooltip(remunerationStatus: RemunerationEnum): boolean {
    if (remunerationStatus === RemunerationEnum.PARTIALLY_REMUNERATED) {
      return true;
    }
    return false;
  }

  public static getRevenueTooltip(
    paidImpressions: number,
    paidPeriodStart: Date,
    paidPeriodEnd: Date,
    translate: TranslateService,
    datePipe: DatePipe
  ): string {
    const lang = translate.currentLang;
    const start =
      datePipe.transform(paidPeriodStart, "mediumDate", undefined, lang) ?? "";
    const end =
      datePipe.transform(paidPeriodEnd, "mediumDate", undefined, lang) ?? "";
      const ms =
      new Date(paidPeriodEnd).getTime() - new Date(paidPeriodStart).getTime();
    const days = Math.round(ms / (1000 * 60 * 60 * 24)) + 1;
    return translate.instant("admin.locations.payment.revenueTooltip", {
      paidImpressions,
      days,
      start,
      end,
    });
  }
}
